import React, { useCallback, useEffect, useState } from 'react'
import '../full.css'
import './OrderConfirmed.css'
import { Navigate } from 'react-router-dom'
import BlockLoader from './BlockLoader'
import { useSearchParams } from 'react-router-dom'

// PayPal occasionally answers a PDT lookup with a redirect or a 5xx, and the
// buyer has already paid by the time they land here — so keep asking for a
// while before giving up. The backend endpoint is idempotent, so a repeated
// call for the same transaction is free.
const RETRY_DELAYS_MS = [1000, 2000, 3000, 5000, 8000, 13000, 21000, 30000]

// The backend does a PDT round trip to PayPal before answering, so allow for a
// slow one — but not forever. Without this a stalled connection (common right
// after the PayPal redirect, on mobile) parks the buyer on the spinner with no
// retry and no way out.
const REQUEST_TIMEOUT_MS = 15000

const SUPPORT_EMAIL = 'admin@visceralsounds.com'

const OrderConfirmed = () => {
  const [searchParams] = useSearchParams()
  const merchantTransactionId = searchParams.get('tx')
  const [state, setState] = useState({ status: 'validating' })

  const validate = useCallback((transaction_id, { onSettled, isCancelled }) => {
    const attempt = async (index) => {
      if (isCancelled()) return

      let outcome = null
      const controller = new AbortController()
      const timeout = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS)

      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_ENDPOINT}/api/order-processing/validatePaypalOrder`,
          {
            method: 'POST',
            body: JSON.stringify({ transaction_id }),
            signal: controller.signal,
          },
        )

        // validatePaypalOrder answers 200 for every outcome it has an opinion
        // on, so a non-2xx is the framework or a proxy failing. Strapi renders
        // those as JSON too, which would otherwise parse into an envelope with
        // no `retryable` and end the retry ladder on the very first attempt.
        outcome = response.ok ? await response.json() : { retryable: true }
      } catch (error) {
        // Network failure, timeout (abort), or a body that isn't JSON.
        outcome = { retryable: true }
      } finally {
        clearTimeout(timeout)
      }

      if (isCancelled()) return

      // A body of `null` parses fine and would throw on the reads below.
      if (!outcome || typeof outcome !== 'object') {
        outcome = { retryable: true }
      }

      if (outcome.response_code === 1 && outcome.payload) {
        onSettled({ status: 'ready', purchase: outcome.payload })
        return
      }

      // The payment is real but hasn't cleared (eCheck, pending review).
      // PayPal notifies us when it does, and the buyer gets an email.
      if (outcome.status === 'pending') {
        onSettled({ status: 'pending' })
        return
      }

      const delay = outcome.retryable ? RETRY_DELAYS_MS[index] : undefined
      if (delay === undefined) {
        onSettled({ status: 'failed' })
        return
      }

      onSettled({ status: 'validating', attempt: index + 1 })
      setTimeout(() => run(index + 1), delay)
    }

    // Nothing above is allowed to reject: an unhandled rejection here would
    // leave the buyer on the spinner for good, rather than on a page that
    // tells them what to do next.
    const run = (index) =>
      attempt(index).catch(() => {
        if (!isCancelled()) onSettled({ status: 'failed' })
      })

    run(0)
  }, [])

  useEffect(() => {
    if (!merchantTransactionId) {
      setState({ status: 'failed' })
      return undefined
    }

    let cancelled = false
    validate(merchantTransactionId, {
      isCancelled: () => cancelled,
      onSettled: setState,
    })

    return () => {
      cancelled = true
    }
  }, [merchantTransactionId, validate])

  if (state.status === 'ready') {
    return (
      <Navigate
        to={`/redeem-purchase/${state.purchase.purchase_id}/${state.purchase.security_key}`}
        replace
      />
    )
  }

  if (state.status === 'pending') {
    return (
      <div style={{ color: 'white' }} className="purchaseContainer">
        <h2 style={{ color: 'white', textAlign: 'center' }}>Your payment is clearing</h2>
        <p style={{ textAlign: 'center' }}>
          Thank you! PayPal hasn't finished clearing this payment yet — some payment methods take a
          few days. As soon as it clears we'll email your download link to you automatically.
        </p>
        {merchantTransactionId ? (
          <p style={{ textAlign: 'center' }}>
            <small>Your PayPal transaction: {merchantTransactionId}</small>
          </p>
        ) : null}
      </div>
    )
  }

  if (state.status === 'failed') {
    return (
      <div style={{ color: 'white' }} className="purchaseContainer">
        <h2 style={{ color: 'white', textAlign: 'center' }}>
          {merchantTransactionId ? 'Your payment went through' : "We couldn't build your link"}
        </h2>
        <p style={{ textAlign: 'center' }}>
          {merchantTransactionId
            ? "We couldn't build your download link on this page, your link should reach you by email within a few minutes. There's nothing you need to do."
            : "If you've just paid, your download link should reach you by email within a few minutes."}
        </p>
        <p style={{ textAlign: 'center' }}>
          If it hasn't arrived within 15 minutes, email us at{' '}
          <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> and we'll sort it out right away.
        </p>
        {merchantTransactionId ? (
          <p style={{ textAlign: 'center' }}>
            <small>Your PayPal transaction ID: {merchantTransactionId}</small>
          </p>
        ) : null}
      </div>
    )
  }

  return (
    <div style={{ color: 'white' }} className="purchaseContainer">
      <h2 style={{ color: 'white', textAlign: 'center' }}>Please Wait...</h2>
      <BlockLoader style={{ height: '10rem' }} />
      <p style={{ textAlign: 'center' }}>
        Please wait a few seconds while we validate your purchase and prepare your download link.
      </p>
    </div>
  )
}

export default OrderConfirmed
